import { Trans, useLingui } from "@lingui/react/macro";
import { type ReactNode } from "react";

import type { TOTPEnrollBeginResponse } from "../../../api";

import { CopyButton } from "../../../components/ui-kit/copy-button";
import { ErrorMessage } from "../../../components/ui-kit/error-message";
import { OtpCodeInput } from "./code-input";

export function TotpEnrollment({
  enrollment,
  intro,
  errorMessage,
  onConfirm,
  onCancel,
  cancelLabel,
}: {
  enrollment: TOTPEnrollBeginResponse;
  /** What to do with the QR code, which differs when replacing an app. */
  intro: ReactNode;
  errorMessage: string;
  onConfirm: (code: string) => Promise<void>;
  onCancel: () => void;
  cancelLabel: ReactNode;
}) {
  const { t } = useLingui();

  return (
    <div className="flex flex-col items-center" data-testid="mfa-totp-setup">
      <h2 className="text-xl font-semibold mb-4">
        <Trans>Set up two-factor authentication</Trans>
      </h2>

      {/* Capped on its own — the column is sized by the QR code, and later by
          the backup codes. */}
      <p className="text-sm text-center max-w-96">{intro}</p>

      <img
        src={enrollment.qr_code_data_uri}
        alt={t`QR code for setting up two-factor authentication`}
        // Natural size: the backend renders ~305-365px depending on how long
        // the account's otpauth URI is, and scaling that down blurs the module
        // edges enough to bother phone cameras.
        className="mt-4 max-w-full rounded-md bg-white"
      />

      <div className="flex flex-col items-center gap-1 mt-3">
        <span className="text-xs text-muted-foreground">
          <Trans>Or enter this key manually:</Trans>
        </span>
        <div className="flex items-center gap-2">
          <code className="text-xs break-all">{enrollment.secret}</code>
          <CopyButton
            value={enrollment.secret}
            iconOnly
            label={t`Copy setup key`}
            copiedLabel={t`Setup key copied`}
          />
        </div>
      </div>

      <OtpCodeInput submitLabel={t`Confirm`} onSubmit={onConfirm} />

      <ErrorMessage message={errorMessage} />

      <a
        onClick={onCancel}
        className="text-xs text-brand-primary hover:underline cursor-pointer mt-8"
      >
        {cancelLabel}
      </a>
    </div>
  );
}
